import type { RouteRecordRaw } from 'vue-router';

/**
 * Rotas.
 *
 * Tudo em lazy loading: o bundle inicial carrega só o necessário para
 * login. Um usuário que nunca abre /admin nunca baixa aquele código.
 *
 * Meta:
 *   requiresAuth       — precisa de sessão
 *   requiresOnboarding — precisa de onboarding concluído + workspace
 *   requiresAdmin      — precisa ser admin da PLATAFORMA (não do workspace)
 *   guestOnly          — quem já está logado não deve ver
 */
export const routes: RouteRecordRaw[] = [
  /**
   * A raiz do domínio é o FUNIL DE ASSINATURA, não uma página de
   * apresentação.
   *
   * Quem digita o endereço já ouviu falar do produto — a home explicava o
   * conceito e escondia a compra atrás de "criar conta", fazendo quem
   * chegou decidido procurar por onde pagar.
   *
   * O funil tem layout próprio (coluna da marca + formulário), então não
   * usa `PublicLayout`: envolvê-lo num cabeçalho de site quebraria a tela
   * cheia que ele precisa.
   */
  {
    path: '/',
    name: 'signup',
    component: () => import('@/pages/signup/SignupFunnel.vue'),
    /**
     * NÃO é `guestOnly`.
     *
     * Quem criou a conta e não pagou precisa VOLTAR aqui para terminar. Se
     * a rota expulsasse todo autenticado para o painel, e o painel
     * devolvesse todo não-pagante para cá, seria um laço infinito de
     * redirecionamento. Quem decide é o guard, pela assinatura.
     */
    meta: { funnel: true },
  },

  /**
   * Documentos legais — PÚBLICOS.
   *
   * O link para eles aparece no formulário de cadastro, antes de existir
   * sessão. Ninguém deveria precisar criar conta para ler o que vai
   * aceitar, e exigir login aqui seria pedir aceite às cegas.
   */
  {
    path: '/termos',
    name: 'terms',
    component: () => import('@/pages/legal/LegalPage.vue'),
  },
  {
    path: '/privacidade',
    name: 'privacy',
    component: () => import('@/pages/legal/LegalPage.vue'),
  },
  {
    path: '/lgpd',
    name: 'lgpd',
    component: () => import('@/pages/legal/LegalPage.vue'),
  },

  /**
   * A antiga home continua acessível em `/sobre`.
   *
   * Ela ainda serve para link em anúncio e para quem quer entender antes
   * de assinar — remover seria perder conteúdo que já existe e funciona,
   * sem ganho nenhum.
   */
  {
    path: '/sobre',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [{ path: '', name: 'landing', component: () => import('@/pages/LandingPage.vue') }],
  },

  {
    path: '/',
    component: () => import('@/layouts/AuthLayout.vue'),
    meta: { guestOnly: true },
    children: [
      { path: 'login', name: 'login', component: () => import('@/pages/auth/LoginPage.vue') },
      /**
       * `/register` não existe mais como página.
       *
       * Ela criava conta FORA do funil — pulando plano e pagamento — e
       * ainda prometia "7 dias grátis, sem cartão de crédito", que é o
       * oposto do que o produto faz desde a mudança para "pagou, ativou".
       * Promessa que o sistema não cumpre é pior que página feia.
       *
       * Redireciona em vez de sumir: o endereço já circulou, e um 404 na
       * porta de entrada perde quem chegou por um link antigo.
       */
      { path: 'register', redirect: { name: 'signup' } },

      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
      },
    ],
  },

  {
    // Fora de guestOnly: o link de redefinição chega com sessão temporária.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [{ path: '', component: () => import('@/pages/auth/ResetPasswordPage.vue') }],
  },

  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/pages/auth/AuthCallbackPage.vue'),
  },

  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/pages/onboarding/OnboardingPage.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    /**
     * `requiresPayment` vale para o painel inteiro.
     *
     * As exceções ficam nas telas filhas, e são poucas de propósito: só
     * onde a pessoa PRECISA entrar justamente para resolver a pendência.
     */
    meta: { requiresAuth: true, requiresOnboarding: true, requiresPayment: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/pages/app/DashboardPage.vue'),
        meta: { title: 'Início', icon: 'home' },
      },
      {
        path: 'assistant',
        name: 'assistant',
        component: () => import('@/pages/app/AssistantPage.vue'),
        meta: { title: 'Assistente', icon: 'forum' },
      },
      {
        path: 'accounts',
        name: 'accounts',
        component: () => import('@/pages/app/AccountsPage.vue'),
        meta: { title: 'Contas bancárias', icon: 'account_balance' },
      },
      {
        path: 'finance',
        name: 'finance',
        component: () => import('@/pages/app/FinancePage.vue'),
        meta: { title: 'Financeiro', icon: 'account_balance_wallet' },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/pages/app/ReportsPage.vue'),
        meta: { title: 'Relatórios', icon: 'insights' },
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/pages/app/CategoriesPage.vue'),
        meta: { title: 'Categorias', icon: 'sell' },
      },
      {
        path: 'goals',
        name: 'goals',
        component: () => import('@/pages/app/GoalsPage.vue'),
        meta: { title: 'Metas', icon: 'savings' },
      },
      {
        path: 'budget',
        name: 'budget',
        component: () => import('@/pages/app/BudgetPage.vue'),
        meta: { title: 'Limites de gastos', icon: 'speed' },
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@/pages/app/CalendarPage.vue'),
        meta: { title: 'Agenda', icon: 'event' },
      },
      {
        path: 'tasks',
        name: 'tasks',
        component: () => import('@/pages/app/TasksPage.vue'),
        meta: { title: 'Tarefas', icon: 'checklist' },
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@/pages/app/DocumentsPage.vue'),
        meta: { title: 'Documentos', icon: 'folder_open' },
      },
      {
        path: 'integrations',
        name: 'integrations',
        component: () => import('@/pages/app/IntegrationsPage.vue'),
        meta: { title: 'Integrações', icon: 'extension' },
      },
      /**
       * Modelos de mensagem — do DONO DA PLATAFORMA.
       *
       * Com um número oficial único, os modelos são aprovados uma vez na
       * conta do Zelo e servem todos os clientes. Não é tela de cliente:
       * se ele apagasse um modelo, quebraria o envio de todo mundo.
       *
       * Esconder no menu não basta — o endereço é digitável. A trava de
       * verdade continua no servidor: a Edge Function exige OWNER ou
       * ADMIN do workspace da conexão, e a RLS só deixa ler o que é do
       * próprio espaço.
       */
      {
        path: 'templates',
        name: 'templates',
        component: () => import('@/pages/app/TemplatesPage.vue'),
        meta: { title: 'Modelos de mensagem', icon: 'chat_bubble_outline', requiresAdmin: true },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/pages/app/BillingPage.vue'),
        /**
         * Aberta mesmo sem assinatura ativa.
         *
         * É a tela onde se regulariza um pagamento vencido ou se reativa
         * uma assinatura cancelada. Bloqueá-la trancaria a pessoa do lado
         * de fora da única porta que resolve o problema dela.
         */
        meta: { title: 'Plano e cobrança', icon: 'credit_card', requiresPayment: false },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/app/SettingsPage.vue'),
        meta: { title: 'Configurações', icon: 'settings' },
      },
    ],
  },

  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'admin-overview',
        component: () => import('@/pages/admin/AdminOverviewPage.vue'),
        meta: { title: 'Visão geral' },
      },
      {
        path: 'receita',
        name: 'admin-revenue',
        component: () => import('@/pages/admin/AdminRevenuePage.vue'),
        meta: { title: 'Receita e clientes' },
      },
      {
        path: 'workspaces',
        name: 'admin-workspaces',
        component: () => import('@/pages/admin/AdminWorkspacesPage.vue'),
        meta: { title: 'Workspaces' },
      },
      {
        path: 'cupons',
        name: 'admin-coupons',
        component: () => import('@/pages/admin/AdminCouponsPage.vue'),
        meta: { title: 'Cupons de desconto' },
      },
    ],
  },

  {
    path: '/:catchAll(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
];
